import { ErrorHandler, NgModule, provideZoneChangeDetection, provideAppInitializer, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from 'src/environments/environment';
import { ServerErrorInterceptor } from './core/interceptors/server-error.interceptor';
import { GlobalErrorHandler } from './core/interceptors/global-error-handler';
import { Store, StoreModule } from '@ngrx/store';
import * as appStore from './main/store';
import {metaReducers} from './main/store';
import {EffectsModule} from '@ngrx/effects';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {CoreModule} from './core/core.module';

import {CommonModule, CurrencyPipe, registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {OktaAuth} from '@okta/okta-auth-js';
import {AuthModule} from './modules/auth/auth.module';
import {OktaAuthModule, OktaConfig} from '@okta/okta-angular';
import {AuthInterceptor} from "./core/authorization/auth.interceptor";
import {PctCalculator} from 'rem-pct-engine-core';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService, MessageService} from 'primeng/api';
import {MessageModule} from 'primeng/message';
import {QuoteServiceManager, QuoteWrapperManager, ServiceLineManager} from 'quote-engine-core';
import {PriceEngineManager} from 'price-engine-core';
import {PriceEngineService} from './core/price-engine/price-engine.service';
import {CorridorBlockService} from './components/corridor/corridor-block.service';
import {CustomGroupV1Mapper} from './components/synthesis-content/mapper/custom-group-v1.mapper';
import {ExtraFeeV1Mapper} from './components/synthesis-content/mapper/extra-fee-v1.mapper';
import {OptionV1Mapper} from './components/synthesis-content/mapper/option-v1.mapper';
import {PricingItemV1Mapper} from './components/synthesis-content/mapper/pricing-item-v1.mapper';
import {QuoteServiceV1Mapper} from './components/synthesis-content/mapper/quote-service-v1.mapper';
import {SynthesisServiceProxy, TranslationServiceInterface} from 'quote-export-manager-core';
import {TranslationProviderService} from './modules/i18n/translation-provider.service';
import {ReferentialService} from "./core/referential/referential.service";
import {referentialServiceFactory} from "./core/referential/referential-service.factory";
import {RxdbRepository} from "./core/rxdb/rxdb.repository";
import {cmcProtocolServiceFactory} from "./core/cmc/_services/cmc-protocol-service.factory";
import {CmcProtocolService} from "./core/cmc/_services/cmc-protocol.service";
import {ServiceLineV1Mapper} from './components/synthesis-content/mapper/service-line-v1.mapper';
import {extraFeeServiceFactory} from "./core/extraFees/_services/extra-fee-service.factory";
import {ExtraFeeService} from "./core/extraFees/_services/extra-fee.service";
import {pctConfigServiceFactory} from "./core/pct-config/_services/pct-config-service.factory";
import {PctConfigService} from "./core/pct-config/_services/pct-config.service";
import {StandaloneModeService} from "./core/standalone-mode.service";
import {quoteManagerServiceFactory} from "./core/quote/_services/quote-manager-service.factory";
import {EppRouterService} from "./core/router/epp-router.service";
import {QuoteServiceService} from "./core/quote-service/quote-service.service";
import {CustomerContractService} from "./core/customer-contract/_services/customer-contract.service";
import {QuoteManagerService} from "./core/quote/_services/quote-manager.service";
import {OptionPricingItemMapper} from "./core/models/quote/pricing-item/mapper/option-pricing-item.mapper";
import {ProductManagerV2Service} from './core/products/_services/product-manager-v2.service';
import {productManagerV2ServiceFactory} from './core/products/_services/product-manager-v2-service.factory';
import {ProductMapperV2Service} from './core/products/_services/product-mapper-v2.service';
import {
    StandaloneQuoteHistoryService
} from './core/standalone-quote-history/_services/standalone-quote-history.service';
import {DefaultComponent} from "./components/default/default.component";
import {LoginCallbackComponent} from './modules/auth/login-callback/login-callback.component';
import {DialogService} from 'primeng/dynamicdialog';
import {UserLoggerService} from './core/app_log/userlog.service';
import {CmcProductManagerService} from './core/products/_services/cmc/cmc-product-manager.service';
import {cmcProductManagerServiceFactory} from "./core/products/_services/cmc/cmc-prodcut-manager-service.factory";
import {CmcProductManagerDatabase} from "./core/sqllite/databases/cmc-product-manager.database";
import {ParametersDatabase} from "./core/sqllite/databases/parameters.database";
import {StandardProductManagerDatabase} from "./core/sqllite/databases/standard-product-manager.database";
import {provideElisMaia} from '@elis/angular';
import {loadFromIndexedDb} from './core/store';
import {HydrateQuoteSuccess} from './core/store/quote';
import localeCs from '@angular/common/locales/cs';
import { ElisTranslatorLoaderFactory } from './modules/i18n/elis-translator-loader-factory';
import { TranslationCacheService } from './modules/i18n/translation-cache.service';
import localePt from '@angular/common/locales/pt';
import localeGa from '@angular/common/locales/ga';
import {opportunityManagerServiceFactory} from './core/opportunity/opportunity-manager-service.factory';
import {OpportunityV3ManagerService} from './core/opportunity/opportunity-v3-manager.service';
import localeSk from '@angular/common/locales/sk';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import localeEs from '@angular/common/locales/es';
import localeIt from '@angular/common/locales/it';
import { provideAngularSvgIcon } from 'angular-svg-icon';

registerLocaleData(localeFr);
registerLocaleData(localeCs);
registerLocaleData(localePt);
registerLocaleData(localeGa);
registerLocaleData(localeSk);
registerLocaleData(localeEn);
registerLocaleData(localePl);
registerLocaleData(localeEs);
registerLocaleData(localeIt);

const oktaAuth = new OktaAuth({
    issuer: environment.oktaConfig.issuer,
    clientId: environment.oktaConfig.clientId,
    redirectUri: environment.oktaConfig.redirectUri,
    logoutUrl: environment.oktaConfig.postLogoutRedirectUri,
    pkce: environment.oktaConfig.pkce,
    scopes: ['openid', 'epp', 'offline_access'],
});
const oktaModuleConfig: OktaConfig = { oktaAuth };


const originalConsole = { ...console };

export function overrideConsole(logger: UserLoggerService): () => void {
    return () => {
        (['log', 'info', 'warn', 'error', 'debug'] as const).forEach(level => {
            const original = originalConsole[level]; // utilise la version d'origine
            console[level] = (...args: any[]) => {
                // Évite la récursion
                logger.capture(level, ...args);
                original.apply(console, args);
            };
        });
    };
}

@NgModule({
    declarations: [AppComponent, DefaultComponent, LoginCallbackComponent],
    bootstrap: [AppComponent],
    exports: [], imports: [BrowserModule,
        MessageModule,
        TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useClass: ElisTranslatorLoaderFactory.forModule(),
            deps: [TranslationCacheService],
        },
        }),
        CoreModule,
        AppRoutingModule,
        InlineSVGModule.forRoot(),
        StoreModule.forRoot(appStore.reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true
            }
        }),
        EffectsModule.forRoot([]),
        StoreRouterConnectingModule.forRoot({
            // stateKey: 'router',
            serializer: appStore.CustomSerializer
        }),
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        OktaAuthModule.forRoot(oktaModuleConfig),
        AuthModule,
        ConfirmDialogModule
    ],
        providers: [
            { provide: PriceEngineManager, useValue: new PriceEngineManager() },
            {
                provide: PriceEngineService,
                useFactory: (manager: PriceEngineManager) => new PriceEngineService(manager),
                deps: [PriceEngineManager]
            },
            { provide: PctCalculator, useValue: new PctCalculator() },
            { provide: QuoteServiceManager, useValue: new QuoteServiceManager() },
            { provide: QuoteWrapperManager, useValue: new QuoteWrapperManager() },
            { provide: ServiceLineManager, useValue: new ServiceLineManager() },
            { provide: CorridorBlockService, useValue: new CorridorBlockService() },
            MessageService,
            DialogService,
            CurrencyPipe,
            ConfirmationService,
            StandaloneQuoteHistoryService,
            { provide: ErrorHandler, useClass: GlobalErrorHandler },
            {
                provide: HTTP_INTERCEPTORS,
                useClass: ServerErrorInterceptor,
                multi: true,
            },
            { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
            CustomGroupV1Mapper,
            ExtraFeeV1Mapper,
            OptionV1Mapper,
            PricingItemV1Mapper,
            QuoteServiceV1Mapper,
            ServiceLineV1Mapper,
            {
                provide: SynthesisServiceProxy,
                useFactory: (translationService: TranslationServiceInterface) => new SynthesisServiceProxy(translationService),
                deps: [TranslationProviderService]
            },
            {
                provide: ReferentialService,
                useFactory: referentialServiceFactory,
                deps: [StandaloneModeService, ParametersDatabase, HttpClient]
            },
            {
                provide: CmcProtocolService,
                useFactory: cmcProtocolServiceFactory,
                deps: [StandaloneModeService, RxdbRepository, HttpClient, TranslateService]
            },
            {
                provide: ProductManagerV2Service,
                useFactory: productManagerV2ServiceFactory,
                deps: [StandaloneModeService, Store, StandardProductManagerDatabase, HttpClient, ProductMapperV2Service]
            },
            {
                provide: ExtraFeeService,
                useFactory: extraFeeServiceFactory,
                deps: [StandaloneModeService, ParametersDatabase, HttpClient]
            },
            {
                provide: PctConfigService,
                useFactory: pctConfigServiceFactory,
                deps: [StandaloneModeService, ParametersDatabase, HttpClient]
            },
            {
                provide: CmcProductManagerService,
                useFactory: cmcProductManagerServiceFactory,
                deps: [StandaloneModeService, HttpClient, ProductMapperV2Service, CmcProductManagerDatabase]
            },
            {
                provide: QuoteManagerService,
                useFactory: quoteManagerServiceFactory,
                deps: [StandaloneModeService, RxdbRepository, HttpClient, EppRouterService, QuoteServiceService, Store, TranslateService, CustomerContractService, ProductManagerV2Service, CmcProductManagerService, OptionPricingItemMapper]
            },
            {
                provide: OpportunityV3ManagerService,
                useFactory: opportunityManagerServiceFactory,
                deps: [StandaloneModeService, RxdbRepository, HttpClient]
            },
            UserLoggerService,
            provideAppInitializer(() => {
                const logger = inject(UserLoggerService);
                const init = overrideConsole(logger);
                init();
            }),
            provideAppInitializer(async () => {
                const store = inject(Store);
                const state = await loadFromIndexedDb('quote');
                
                if (state) {
                    store.dispatch(new HydrateQuoteSuccess(state));
                }
            }),
            provideHttpClient(withInterceptorsFromDi()),
            provideZoneChangeDetection(),
            provideElisMaia(),
            provideAngularSvgIcon()
        ]
})
export class AppModule {
}
